import { GridColumns } from '@mui/x-data-grid-pro'
import { ProductOrderDto } from '@services/dtos'
import { useEffect, useMemo, useState } from 'react'
import { useAppDispatch } from '@hooks/reduxHooks'
import { declineOrder, getProductOrdersToValidate, setValidationFilters, validateOrder } from '@state/reducers/productOrderReducer'
import { AppDispatch, useSelector } from '@state/store'
import BaseConfigTable from './BaseConfigTable'
import { productOrdersToValidateColumns } from '@utils/gridColumns'
import { Cancel, CheckCircle } from '@mui/icons-material'
import ConfirmDialog from '../dialogs/ConfirmDialog'
import { Tooltip } from '@mui/material'

export default function ProductOrderValidationsTable() {
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null)
  const [isApprovedConfirmDialogOpen, setIsApprovedConfirmDialogOpen] = useState(false)
  const [isDeclinedConfirmDialogOpen, setIsDeclinedConfirmDialogOpen] = useState(false)
  const dispatch = useAppDispatch()
  const { productOrdersToValidate } = useSelector(({ productOrder }) => ({
    productOrdersToValidate: productOrder.validation.items.datas
  }))
  useEffect(() => {
    return () => {
      dispatch(setValidationFilters({}))
    }
  }, [])

  const selectedItem = useMemo(
    () => productOrdersToValidate.find(({ id }) => id === selectedItemId) ?? null,
    [productOrdersToValidate, selectedItemId]
  )
  
  const columns: GridColumns<ProductOrderDto> = useMemo(
    () => [
      {
        field: '__actions__',
        headerName: 'Actions',
        sortable: false,
        width: 90,
        renderCell: (params: any) => {
          const row = params.row as ProductOrderDto
          const actions = [
            {
              key: 'approveOrder',
              label: 'Approuver',
              icon: <CheckCircle />,
              onClick: () => {
                setSelectedItemId(row.id)
                setIsApprovedConfirmDialogOpen(true)
              },
            },
            {
              key: 'declineOrder',
              label: 'Décliner',
              icon: <Cancel />,
              onClick: () => {
                setSelectedItemId(row.id)
                setIsDeclinedConfirmDialogOpen(true)
              },
            },
          ]
          return (
            <div className="flex space-x-2">
              {actions.map((a) => (
                <Tooltip key={a.key} title={a.label}>
                  <div
                    className={'cursor-pointer p-1 hover:bg-gray-200 rounded'}
                    onClick={(e) => {
                      e.stopPropagation()
                      a.onClick()
                    }}
                  >
                    {a.icon}
                  </div>
                </Tooltip>
              ))}
            </div>
          )
        },
      },
      ...productOrdersToValidateColumns
    ],
    [],
  )

  return (
    <>
      <BaseConfigTable<ProductOrderDto>
        columns={columns}
        selectRows={(state) => state.productOrder.validation.items}
        fetchThunk={() => async (dispatch: AppDispatch) => {
          return dispatch(getProductOrdersToValidate())
        }}
        withActions={false}
        updatePageThunk={(page) => async (dispatch: AppDispatch) => {
          dispatch(setValidationFilters({ page }))
          dispatch(getProductOrdersToValidate())
        }}
        removeToolbar
      />

      {selectedItem && <ConfirmDialog
        open={isApprovedConfirmDialogOpen}
        title="Approbation de la commande"
        message={`Êtes-vous sûr d'approuver la commande ${selectedItem.internalOrderNumber} du 
          ${new Date(selectedItem.createdAt).toLocaleDateString('fr-FR')} à ${selectedItem.supplier.name} ?`}
        onCancel={() => setIsApprovedConfirmDialogOpen(false)}
        onConfirm={() => {
          setIsApprovedConfirmDialogOpen(false)
          dispatch(validateOrder(selectedItem.id))
        }}
      />}

      {selectedItem && <ConfirmDialog
        open={isDeclinedConfirmDialogOpen}
        title="Décliner la commande"
        message={`Êtes-vous sûr de décliner la commande ${selectedItem.internalOrderNumber} du 
          ${new Date(selectedItem.createdAt).toLocaleDateString('fr-FR')} à ${selectedItem.supplier.name} ?`}
        onCancel={() => setIsDeclinedConfirmDialogOpen(false)}
        onConfirm={() => {
          setIsDeclinedConfirmDialogOpen(false)
          dispatch(declineOrder(selectedItem.id))
        }}
      />}
    </>
  )
}
